// animatorCardStatus — the join between a bulk-animate batch's /active items
// (keyed by boxId) and AnimatorPanel's per-scene image cards, plus the
// per-item running/done/failed derivation both the card grid and the
// scene-header progress count share. Extracted so a future rename of either
// side's field (boxId, status, resultUrl) fails a test instead of silently
// rendering every card as "not animated" with no visible progress.

export function buildLiveByBoxId(items) {
  const map = {};
  (items || []).forEach((it) => {
    map[it.boxId] = it;
  });
  return map;
}

export function deriveCardLiveState(live) {
  // fail|cancel, not just fail: a cancelled item never gets a resultUrl
  // either, so without "cancel" here it would fall through to isRunning and
  // a cancelled batch's cards would spin forever instead of showing failed.
  const isFailed = !!live?.status && /fail|cancel/i.test(live.status);
  const isDone = !!live?.resultUrl;
  const isRunning = !!live && !isDone && !isFailed;
  return { isFailed, isDone, isRunning };
}

// Which element a result url should render as. mp4/webm need <video>; webp/
// gif (or anything else, including the future default) render as <img> --
// animated webp/gif loop natively with no JS. Extension-based and never
// assumes "new batches are webp" -- a training's animation history can mix
// today's mp4 results with tomorrow's looping webp ones (see the middleware's
// parallel default-output change), so every card checks its own url.
import { assetKey } from "../../common/utils";

const VIDEO_URL_RE = /\.(mp4|webm)(\?|$)/i;
export function isVideoUrl(url) {
  return VIDEO_URL_RE.test(url || "");
}

const stripQuery = assetKey;

// Is this specific animation result already showing on its box, right now?
// Neither preflight nor /active carries an "applied" flag (alreadyAnimated
// only means "an animation exists in the library" -- see AnimatorPanel.jsx's
// file header) -- this is the one place AnimatorPanel reads live Redux
// (pluginToolbarsById) instead of the API, and only to answer this single
// question. Two cases:
//  (a) boxId still resolves and its state.url already IS resultUrl -- covers
//      a plain url update: the future webp/gif default (never box-swaps,
//      see applyAnimationResult.es6), or a box that was already a non-image
//      plugin before this apply.
//  (b) boxId no longer resolves -- a forward apply of a video result onto a
//      HotspotImages box deletes it and creates a brand-new EnrichedPlayer
//      box with an unrelated id (applyAnimationResult.es6's boxSwap). The
//      only surviving join is the originalImageUrl stash that swap writes on
//      the new box, keyed by the source image's own sourceUrl.
// Swaps performed before that stash existed (see revertAnimatedBox.es6's
// fetchFileVersions fallback) have no such join and can't be detected here --
// they fall through to `false`, the safer of the two wrong answers: it never
// hides a real Approve action, it can only offer one that's a harmless no-op.
export function isResultApplied({ boxId, sourceUrl, resultUrl, pluginToolbarsById }) {
  if (!resultUrl) return false;
  const toolbars = pluginToolbarsById || {};
  const direct = toolbars[boxId];
  if (direct && stripQuery(direct.state?.url) === stripQuery(resultUrl)) return true;
  const swapped = Object.values(toolbars).find(
    (tb) =>
      tb?.pluginId === "EnrichedPlayer" &&
      stripQuery(tb.state?.originalImageUrl) === stripQuery(sourceUrl)
  );
  return !!swapped && stripQuery(swapped.state?.url) === stripQuery(resultUrl);
}

// Find the animation currently applied to an image's box in live Redux,
// returning { boxId, url } of the swapped video box, or null. This is the
// inverse of isResultApplied's case (b): applyAnimationResult swaps an animated
// HotspotImages box for a new EnrichedPlayer whose state.originalImageUrl
// stashes the source image url (see applyAnimationResult.es6). Matching that
// stash to sourceUrl lets AnimatorPanel derive the "Applied" state from the
// saved canvas alone, WITHOUT depending on preflight's server-side
// alreadyAnimated match (which can miss) — so a box that is animated + applied
// still renders as such after a refresh. The returned boxId is the NEW
// EnrichedPlayer id (the swap mints a fresh id), which a preflight card still
// keyed by the original image's boxId needs in order to target Revert at the
// right box. url-update applies (webp/gif on the same box) carry no such stash
// and can't be told apart from an untouched image here, so they fall through
// to null — preflight stays their only signal.
export function findAppliedAnimation({ sourceUrl, pluginToolbarsById }) {
  if (!sourceUrl) return null;
  const toolbars = pluginToolbarsById || {};
  const entry = Object.entries(toolbars).find(
    ([, tb]) =>
      tb?.pluginId === "EnrichedPlayer" &&
      stripQuery(tb.state?.originalImageUrl) === stripQuery(sourceUrl)
  );
  if (!entry) return null;
  return { boxId: entry[0], url: entry[1].state?.url || null };
}

// Per-card single-image "Animate"/"Re-animate" button (bulk-panel task 37,
// ASK 2/3): opens the normal ImageAnimatorModal popup for just this image
// instead of the old bulk-of-one start(), so it's no longer subject to the
// one-batch-per-training lock and stays available while a batch runs -- EXCEPT
// for a card whose own item is currently running inside that batch, where
// offering it would double-generate the same image (the running spinner/badge
// already communicates that state, so the button is hidden, not disabled).
// `animatable` gates enablement, not visibility: a non-animatable source still
// shows the button, disabled with a reason, instead of a dead click or a
// silently vanished action.
export function deriveCardAnimateAction({ isAnimated, isRunning, animatable }) {
  if (isRunning) return { show: false, disabled: false, reanimate: false };
  return { show: true, disabled: !animatable, reanimate: !!isAnimated };
}
